(() => {
    const SELECTOR = '.product-drop-counter';
    const TIME_ZONE = 'America/Los_Angeles';

    const pad2 = (n) => String(Math.max(0, n)).padStart(2, '0');

    const getPacificParts = (date) => {
        const dtf = new Intl.DateTimeFormat('en-US', {
            timeZone: TIME_ZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
        });

        const parts = dtf.formatToParts(date);
        const map = {};

        for (let i = 0; i < parts.length; i += 1) {
            map[parts[i].type] = parts[i].value;
        }

        return {
            year: parseInt(map.year, 10),
            month: parseInt(map.month, 10),
            day: parseInt(map.day, 10),
            hour: parseInt(map.hour, 10),
            minute: parseInt(map.minute, 10),
            second: parseInt(map.second, 10)
        };
    };

    const getPacificWeekday = (date) => {
        const dtf = new Intl.DateTimeFormat('en-US', {
            timeZone: TIME_ZONE,
            weekday: 'short'
        });

        const wd = dtf.format(date);
        const map = {
            Sun: 0,
            Mon: 1,
            Tue: 2,
            Wed: 3,
            Thu: 4,
            Fri: 5,
            Sat: 6
        };

        return map[wd];
    };

    const addDaysToYmd = (year, month, day, daysToAdd) => {
        const dt = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
        dt.setUTCDate(dt.getUTCDate() + daysToAdd);

        return {
            year: dt.getUTCFullYear(),
            month: dt.getUTCMonth() + 1,
            day: dt.getUTCDate()
        };
    };

    const pacificLocalToUtcTimestamp = (year, month, day, hour, minute, second) => {
        const approxUtc = Date.UTC(year, month - 1, day, hour, minute, second);
        const approxDate = new Date(approxUtc);

        const pac = getPacificParts(approxDate);
        const pacAsUtc = Date.UTC(pac.year, pac.month - 1, pac.day, pac.hour, pac.minute, pac.second);

        const offsetMs = pacAsUtc - approxUtc;

        return approxUtc - offsetMs;
    };

    const getNextWednesdayPacificTimestamp = () => {
        const now = new Date();
        const pacNowParts = getPacificParts(now);
        const pacNowWeekday = getPacificWeekday(now);

        let daysUntil = (3 - pacNowWeekday + 7) % 7;

        if (daysUntil === 0) {
            const isPastTime =
                pacNowParts.hour > 12 ||
                (pacNowParts.hour === 12 && pacNowParts.minute > 0) ||
                (pacNowParts.hour === 12 && pacNowParts.minute === 0 && pacNowParts.second > 0);

            if (isPastTime) {
                daysUntil = 7;
            }
        }

        const targetYmd = addDaysToYmd(
            pacNowParts.year,
            pacNowParts.month,
            pacNowParts.day,
            daysUntil
        );

        return pacificLocalToUtcTimestamp(
            targetYmd.year,
            targetYmd.month,
            targetYmd.day,
            12,
            0,
            0
        );
    };

    const getNextWeekdayAtPacificTimeTimestamp = (weekdayIdx, hour, minute) => {
        const h = Number.isFinite(hour) ? hour : 12;
        const m = Number.isFinite(minute) ? minute : 0;

        const now = new Date();
        const pacNowParts = getPacificParts(now);
        const pacNowWeekday = getPacificWeekday(now);

        let daysUntil = (weekdayIdx - pacNowWeekday + 7) % 7;

        if (daysUntil === 0) {
            const isPastTime =
                pacNowParts.hour > h ||
                (pacNowParts.hour === h && pacNowParts.minute > m) ||
                (pacNowParts.hour === h && pacNowParts.minute === m && pacNowParts.second > 0);

            if (isPastTime) {
                daysUntil = 7;
            }
        }

        const targetYmd = addDaysToYmd(
            pacNowParts.year,
            pacNowParts.month,
            pacNowParts.day,
            daysUntil
        );

        return pacificLocalToUtcTimestamp(
            targetYmd.year,
            targetYmd.month,
            targetYmd.day,
            h,
            m,
            0
        );
    };

    const dayToIndex = (day) => {
        const map = {
            sunday: 0,
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6
        };

        return map[String(day || '').toLowerCase()];
    };

    // EDC-32 - minutes since Sunday 00:00 Pacific time, the same unit
    // snippets/drop-unlock.liquid uses to decide whether a product is open.
    const getPacificMinutesOfWeek = () => {
        const now = new Date();
        const parts = getPacificParts(now);

        return (getPacificWeekday(now) * 1440) + (parts.hour * 60) + parts.minute;
    };

    // EDC-32 - reload the page once the customer's unlock time has passed, so
    // the product appears without a manual refresh. The unlock parameter makes
    // Shopify render the page afresh instead of serving a cached copy. Reloads
    // are spaced out and capped per page and session: a page that still comes
    // back locked (a device clock that is ahead, say) shows zeros instead of
    // reloading forever.
    const UNLOCK_PARAM = 'unlock';
    const UNLOCK_MAX_RELOADS = 3;
    const UNLOCK_RELOAD_GAP_MS = 15000;
    let unlockReloadStarted = false;

    const reloadForUnlock = () => {
        if (unlockReloadStarted) {
            return;
        }

        const storageKey = `edc32-unlock-reloads:${window.location.pathname}`;

        try {
            const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
            const count = Number(saved.count) || 0;
            const last = Number(saved.last) || 0;

            if (count >= UNLOCK_MAX_RELOADS || Date.now() - last < UNLOCK_RELOAD_GAP_MS) {
                return;
            }

            window.sessionStorage.setItem(storageKey, JSON.stringify({ count: count + 1, last: Date.now() }));
        } catch (error) {
            // No session storage: allow a single reload, recognised by the parameter.
            if (new URLSearchParams(window.location.search).has(UNLOCK_PARAM)) {
                return;
            }
        }

        unlockReloadStarted = true;

        const url = new URL(window.location.href);
        url.searchParams.set(UNLOCK_PARAM, String(Date.now()));
        window.location.replace(url.toString());
    };

    const getProductTitleText = () => {
        const el = document.querySelector('h1');

        if (!el) {
            return '';
        }

        return String(el.textContent || '').trim();
    };

    const parseDropDateFromTitleToTargetTs = (titleText) => {
        if (!titleText || typeof titleText !== 'string') {
            return null;
        }

        const txt = titleText.trim();

        let match = /Drop Date\s*\((\d{4})\/(\d{2})\/(\d{2})\)/i.exec(txt);
        if (match) {
            const year = Number.parseInt(match[1], 10);
            const month = Number.parseInt(match[2], 10);
            const day = Number.parseInt(match[3], 10);

            const valid =
                Number.isFinite(year) && year >= 1970 &&
                Number.isFinite(month) && month >= 1 && month <= 12 &&
                Number.isFinite(day) && day >= 1 && day <= 31;

            if (!valid) {
                return null;
            }

            return pacificLocalToUtcTimestamp(year, month, day, 12, 0, 0);
        }

        match = /Drop Date\s*\((\d{2})\/(\d{2})\)/i.exec(txt);
        if (match) {
            const month = Number.parseInt(match[1], 10);
            const day = Number.parseInt(match[2], 10);

            const valid =
                Number.isFinite(month) && month >= 1 && month <= 12 &&
                Number.isFinite(day) && day >= 1 && day <= 31;

            if (!valid) {
                return null;
            }

            const now = new Date();
            const pacNow = getPacificParts(now);

            let year = pacNow.year;

            const isEarlierThisYear =
                month < pacNow.month ||
                (month === pacNow.month && day < pacNow.day);

            const isSameDayPastNoon =
                month === pacNow.month &&
                day === pacNow.day &&
                (
                    pacNow.hour > 12 ||
                    (pacNow.hour === 12 && pacNow.minute > 0) ||
                    (pacNow.hour === 12 && pacNow.minute === 0 && pacNow.second > 0)
                );

            if (isEarlierThisYear || isSameDayPastNoon) {
                year += 1;
            }

            return pacificLocalToUtcTimestamp(year, month, day, 12, 0, 0);
        }

        return null;
    };

    const parseSettingsOrFallback = (root) => {
        const counterType = String(root.dataset.counterType || '').toLowerCase();
        const hasEarlyAccess = String(root.dataset.hasEarlyAccess || '').toLowerCase() === 'true';

        if (counterType === 'title_counter') {
            const titleText = getProductTitleText();
            const parsedFromTitle = parseDropDateFromTitleToTargetTs(titleText);

            if (parsedFromTitle) {
                return {
                    targetTs: hasEarlyAccess ? parsedFromTitle - (24 * 60 * 60 * 1000) : parsedFromTitle
                };
            }
        }

        // EDC-32 - the server works out when this product opens for this
        // customer (RYU, Wani, early access, Kappa, public) and hands it over.
        const targetIdx = dayToIndex(root.dataset.targetDay);

        if (Number.isFinite(targetIdx)) {
            const targetHour = Number.parseInt(root.dataset.targetHour, 10);
            const targetMinute = Number.parseInt(root.dataset.targetMinute, 10);
            const unlockState = String(root.dataset.unlockState || '').toLowerCase();

            // The page was rendered as locked, but this customer's time has
            // already passed this week - it was rendered just before the
            // unlock, or Shopify served an older cached copy. Say so, rather
            // than rolling the countdown over to next week.
            if (unlockState === 'locked') {
                const hour = Number.isFinite(targetHour) ? targetHour : 12;
                const minute = Number.isFinite(targetMinute) ? targetMinute : 0;
                const targetMinutesOfWeek = (targetIdx * 1440) + (hour * 60) + minute;

                if (getPacificMinutesOfWeek() >= targetMinutesOfWeek) {
                    return {
                        targetTs: Date.now(),
                        unlocked: true
                    };
                }
            }

            return {
                targetTs: getNextWeekdayAtPacificTimeTimestamp(targetIdx, targetHour, targetMinute)
            };
        }

        if (hasEarlyAccess) {
            const eaIdx = dayToIndex(root.dataset.earlyAccessDay);
            const eaHour = Number.parseInt(root.dataset.earlyAccessHour, 10);
            const eaMinute = Number.parseInt(root.dataset.earlyAccessMinute, 10);

            if (Number.isFinite(eaIdx)) {
                return {
                    targetTs: getNextWeekdayAtPacificTimeTimestamp(eaIdx, eaHour, eaMinute)
                };
            }
        }

        const startIdx = dayToIndex(root.dataset.dropStartDay);
        const startHour = Number.parseInt(root.dataset.dropStartHour, 10);
        const startMinute = Number.parseInt(root.dataset.dropStartMinute, 10);

        if (Number.isFinite(startIdx)) {
            return {
                targetTs: getNextWeekdayAtPacificTimeTimestamp(startIdx, startHour, startMinute)
            };
        }

        return {
            targetTs: getNextWednesdayPacificTimestamp()
        };
    };

    const initCounter = (root) => {
        const daysEl = root.querySelector('[data-unit="days"]');
        const hoursEl = root.querySelector('[data-unit="hours"]');
        const minsEl = root.querySelector('[data-unit="mins"]');
        const secsEl = root.querySelector('[data-unit="secs"]');

        const render = (diffMs) => {
            let totalSeconds = Math.floor(diffMs / 1000);

            if (totalSeconds < 0) {
                totalSeconds = 0;
            }

            const days = Math.floor(totalSeconds / 86400);
            let rem = totalSeconds % 86400;

            const hours = Math.floor(rem / 3600);
            rem %= 3600;

            const mins = Math.floor(rem / 60);
            const secs = rem % 60;

            if (daysEl) {
                daysEl.textContent = pad2(days);
            }

            if (hoursEl) {
                hoursEl.textContent = pad2(hours);
            }

            if (minsEl) {
                minsEl.textContent = pad2(mins);
            }

            if (secsEl) {
                secsEl.textContent = pad2(secs);
            }
        };

        const tick = () => {
            const { targetTs, unlocked } = parseSettingsOrFallback(root);
            const diff = targetTs - Date.now();

            render(diff);

            if (unlocked) {
                reloadForUnlock();
            }
        };

        tick();
        window.setInterval(tick, 1000);
    };

    const boot = () => {
        const root = document.querySelector(SELECTOR);

        if (!root) {
            return;
        }

        initCounter(root);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
        return;
    }

    boot();
})();